import React, {createContext, useContext, useEffect, useReducer, useMemo, useCallback} from "react";
import {useHistory} from "react-router-dom";
import moment from "moment";
import {useStore} from "hooks/useStore";
import {useTranslation} from "react-i18next";
import {courierTypes, activationSteps, activationSubSteps} from "constants/constants";
import {isConstantExist} from "utils/helpers";
import {useForm} from "hooks/useForm";
import {useLocation} from "react-router-dom";

export const CourierFilterContext = createContext();

export const useCourierFilter = () => {
    const context = useContext(CourierFilterContext);
    return context;
};

export const useCourierFilterProvider = () => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation(["courierPage", "common", "activationSteps", "activationSubSteps"]);

    // VGT-329: Acente kullanıcısı sadeleştirilmiş filtre ve sütun setini görür.
    // Acente-İK aynı sadeleştirilmiş görünümü kullanır.
    const isAgency = ["agency", "agencyIk"].includes(stores.meStore.role);
    // Yönetici Firma filtresi yalnızca admin (ve superadmin) için görünür.
    const isAdmin = stores.meStore.checkRole("isAdmin");

    const initalState = {
        currentPage: 1,
        activity: "all",
        types: [],
        activationStep: "all",
        activationSubStep: "all",
        // VGT-216-1: İşbaşı Eğitimi/Onaylanmış seçiliyken görünen alt-filtreler
        trainingStatus: undefined,
        leaveStatus: "all",
        cityId: undefined,
        continent: undefined,
        searchText: "",
        vehicleInfo: "",
        vehicleInfoText: "",
        tckn: "",
        tcknText: "",
        tcknError: "",
        text: "",
        sort: ["applicationDate_desc"],
        preferredCity: undefined,
        preferredContinent: undefined,
        districts: [],
        preferredDistricts: [],
        selectedDateRange: [moment().startOf("month"), moment()],
        walletOwnershipFilter: "all",
        vehicleInfoError: "",
        callStatuses: ["all"],
        callStatus: "all",
        contractSigningStatus: "all",
        motorTypeIds: [],
        drivingLicenseIds: [],
        workingModelId: [],
        hasCriminalRecord: undefined,
        is20Old: undefined,
        personalCompanySetupStatus: "all",
        autoValidationStatus: "all",
        recall: false,
        zoneManagementId: undefined,
        zoneId: undefined,
        employmentType: undefined,
        managerCompany: "all"
    };

    const location = useLocation();

    const getAllParameters = () => {
        const allStringParams = location?.search || "";

        const parsedAllParams = allStringParams?.split("?");
        const stringParams = parsedAllParams?.[1];

        const getAllParams = stringParams?.split("&");
        const params = getAllParams?.reduce((acc, param) => {
            const [key, value] = param?.split("=");
            acc[key] = value;
            return acc;
        }, {});
        return params;
    };

    const getDefaultState = useCallback(() => {
        const initalDefaultState = {
            ...initalState
        };

        const params = getAllParameters();
        if (params?.page) {
            initalDefaultState.currentPage = parseInt(params?.page);
        }
        if (params?.activity) {
            initalDefaultState.activity = params.activity;
        }
        if (params?.cityId) {
            initalDefaultState.cityId = params.cityId;
        }
        if (params?.continent) {
            initalDefaultState.continent = params.continent?.toLocaleLowerCase("tr-TR");
        }
        if (params?.preferredCity) {
            initalDefaultState.preferredCity = params.preferredCity?.toLocaleLowerCase("tr-TR");
        }
        if (params?.preferredContinent) {
            initalDefaultState.preferredContinent = params.preferredContinent?.toLocaleLowerCase(
                "tr-TR"
            );
        }
        if (params?.preferredDistricts) {
            initalDefaultState.preferredDistricts = params.preferredDistricts.split("-");
        }
        if (params?.walletOwnership) {
            initalDefaultState.walletOwnershipFilter = params.walletOwnership;
        }
        if (params?.activationStep && isConstantExist(activationSteps, params.activationStep)) {
            initalDefaultState.activationStep = params.activationStep;
        }
        if (
            params?.activationSubStep &&
            isConstantExist(activationSubSteps, params.activationSubStep)
        ) {
            initalDefaultState.activationSubStep = params.activationSubStep;
        }
        if (params?.trainingStatus) {
            initalDefaultState.trainingStatus = params.trainingStatus;
        }
        if (params?.leaveStatus) {
            initalDefaultState.leaveStatus = params.leaveStatus;
        }
        if (params?.callStatus) {
            initalDefaultState.callStatus = params.callStatus;
        }
        if (params?.contractSigningStatus) {
            initalDefaultState.contractSigningStatus = params.contractSigningStatus;
        }
        if (params?.motorTypeIds) {
            initalDefaultState.motorTypeIds = params.motorTypeIds.split("-");
        }
        if (params?.drivingLicenseIds) {
            initalDefaultState.drivingLicenseIds = params.drivingLicenseIds.split("-");
        }
        if (params?.workingModelId) {
            initalDefaultState.workingModelId = params.workingModelId.split("-");
        }
        if (params?.hasCriminalRecord) {
            initalDefaultState.hasCriminalRecord = params.hasCriminalRecord;
        }
        if (params?.is20Old) {
            initalDefaultState.is20Old = params.is20Old;
        }
        if (params?.personalCompanySetupStatus) {
            initalDefaultState.personalCompanySetupStatus = params.personalCompanySetupStatus;
        }
        if (params?.autoValidationStatus) {
            initalDefaultState.autoValidationStatus = params.autoValidationStatus;
        }
        if (params?.zoneManagementId) {
            initalDefaultState.zoneManagementId = params.zoneManagementId;
        }
        if (params?.zoneId) {
            initalDefaultState.zoneId = params.zoneId;
        }
        if (params?.employmentType) {
            initalDefaultState.employmentType = params.employmentType;
        }
        if (params?.managerCompany) {
            initalDefaultState.managerCompany = params.managerCompany;
        }
        return {
            ...initalDefaultState
        };
    }, []);

    const [state, dispatch] = useForm(getDefaultState());

    const initializeFiltersFromUrl = useCallback(() => {
        dispatch({type: "recall", payload: !state.recall});
    }, [state.recall]);

    const setUrl = () => {
        let path = history.location.pathname;
        if (state.currentPage) {
            path += "?page=" + state.currentPage;
        }
        if (state.activity) {
            path += "&activity=" + state.activity;
        }
        if (state.types.length) {
            path += "&types=" + state.types.join("-");
        }
        if (state.cityId) {
            path += "&cityId=" + state.cityId;
        }
        if (state.continent) {
            path += "&continent=" + state.continent;
        }
        if (state.preferredCity) {
            path += "&preferredCity=" + state.preferredCity;
        }
        if (state.preferredContinent) {
            path += "&preferredContinent=" + state.preferredContinent;
        }
        if (state.preferredDistricts?.length) {
            path += "&preferredDistricts=" + state.preferredDistricts.join("-");
        }
        if (state.vehicleInfo) {
            path += "&vehicleInfo=" + state.vehicleInfo;
        }
        if (state.tckn) {
            path += "&tckn=" + state.tckn;
        }
        if (state.walletOwnershipFilter) {
            path += "&walletOwnership=" + state.walletOwnershipFilter;
        }
        if (state.activationStep) {
            path += "&activationStep=" + state.activationStep;
        }
        if (state.activationSubStep) {
            path += "&activationSubStep=" + state.activationSubStep;
        }
        if (state.trainingStatus) {
            path += "&trainingStatus=" + state.trainingStatus;
        }
        if (state.leaveStatus && state.leaveStatus !== "all") {
            path += "&leaveStatus=" + state.leaveStatus;
        }
        if (state.callStatus) {
            path += "&callStatus=" + state.callStatus;
        }
        if (state.contractSigningStatus && state.contractSigningStatus !== "all") {
            path += "&contractSigningStatus=" + state.contractSigningStatus;
        }
        if (state.motorTypeIds.length) {
            path += "&motorTypeIds=" + state.motorTypeIds.join("-");
        }
        if (state.drivingLicenseIds.length) {
            path += "&drivingLicenseIds=" + state.drivingLicenseIds.join("-");
        }
        if (state.workingModelId.length) {
            path += "&workingModelId=" + state.workingModelId.join("-");
        }
        if (state.autoValidationStatus && state.autoValidationStatus !== "all") {
            path += "&autoValidationStatus=" + state.autoValidationStatus;
        }
        if (state.personalCompanySetupStatus && state.personalCompanySetupStatus !== "all") {
            path += "&personalCompanySetupStatus=" + state.personalCompanySetupStatus;
        }
        if (state.zoneManagementId) {
            path += "&zoneManagementId=" + state.zoneManagementId;
        }
        if (state.zoneId) {
            path += "&zoneId=" + state.zoneId;
        }
        if (state.employmentType) {
            path += "&employmentType=" + state.employmentType;
        }
        if (state.managerCompany && state.managerCompany !== "all") {
            path += "&managerCompany=" + state.managerCompany;
        }
        history.push(path);
    };

    // Handler Functions
    const handleDateChange = value => {
        dispatch({type: "currentPage", payload: 1});
        if (!value || value.length === 0) {
            dispatch({type: "selectedDateRange", payload: []});
        } else {
            dispatch({type: "selectedDateRange", payload: value});
        }
    };

    const resetFilters = () => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                selectedDateRange: [],
                currentPage: 1,
                activity: "all",
                types: [],
                cityId: undefined,
                continent: undefined,
                districts: [],
                preferredCity: undefined,
                preferredContinent: undefined,
                preferredDistricts: [],
                motorTypeIds: [],
                drivingLicenseIds: [],
                activationStep: "all",
                activationSubStep: undefined,
                trainingStatus: undefined,
                leaveStatus: "all",
                callStatus: undefined,
                contractSigningStatus: "all",
                workingModelId: [],
                is20Old: undefined,
                hasCriminalRecord: undefined,
                autoValidationStatus: "all",
                personalCompanySetupStatus: "all",
                zoneManagementId: undefined,
                zoneId: undefined,
                employmentType: undefined,
                managerCompany: "all"
            }
        });
    };

    const renderCallStatusOptions = ({callStatuses: statuses}) => {
        const SelectField = require("../newCourierStyles").SelectField;
        return statuses?.map(status => (
            <SelectField.Option key={status} value={status}>
                {t(`callStatuses.${status}`, {
                    ns: "common",
                    defaultValue: status
                })}
            </SelectField.Option>
        ));
    };

    const fetchCallStatuses = async () => {
        try {
            const result = await stores.courierStore.getCourierCallStatuses();
            dispatch({type: "callStatuses", payload: result?.result || []});
        } catch (err) {
            console.error("Call statuses could not be fetched:", err);
        }
    };

    const getFilterParams = () => ({
        cityId: state.cityId,
        continent: state.continent,
        preferredCity: state.preferredCity,
        preferredContinent: state.preferredContinent,
        types: state.types,
        searchText: state.searchText,
        vehicleInfo: state.vehicleInfoText,
        tckn: state.tcknText,
        activity: state.activity,
        sort: state.sort,
        activationStep: state.activationStep,
        activationSubStep: state.activationSubStep,
        trainingStatus: state.trainingStatus,
        leaveStatus: state.leaveStatus,
        districts: state.districts,
        preferredDistricts: state.preferredDistricts,
        selectedDateRange: state.selectedDateRange,
        walletOwnershipFilter: state.walletOwnershipFilter,
        callStatus: state.callStatus,
        contractSigningStatus: state.contractSigningStatus,
        motorTypeIds: state.motorTypeIds,
        drivingLicenseIds: state.drivingLicenseIds,
        workingModelId: state.workingModelId,
        hasCriminalRecord: state.hasCriminalRecord,
        is20Old: state.is20Old,
        personalCompanySetupStatus: state.personalCompanySetupStatus,
        autoValidationStatus: state.autoValidationStatus,
        zoneManagementId: state.zoneManagementId,
        zoneId: state.zoneId,
        employmentType: state.employmentType,
        managerCompany: state.managerCompany
    });

    const filterDependencies = [
        state.currentPage,
        state.types,
        state.cityId,
        state.continent,
        state.preferredCity,
        state.preferredContinent,
        state.searchText,
        state.vehicleInfo,
        state.tckn,
        state.activity,
        state.sort,
        state.activationStep,
        state.activationSubStep,
        state.trainingStatus,
        state.leaveStatus,
        state.districts,
        state.preferredDistricts,
        state.selectedDateRange,
        state.walletOwnershipFilter,
        state.callStatus,
        state.contractSigningStatus,
        state.motorTypeIds,
        state.drivingLicenseIds,
        state.workingModelId,
        state.recall,
        state.hasCriminalRecord,
        state.is20Old,
        state.personalCompanySetupStatus,
        state.autoValidationStatus,
        state.zoneManagementId,
        state.zoneId,
        state.employmentType,
        state.managerCompany
    ];

    const urlSyncDependencies = [
        state.currentPage,
        state.types,
        state.cityId,
        state.continent,
        state.preferredCity,
        state.preferredContinent,
        state.preferredDistricts,
        state.vehicleInfo,
        state.tckn,
        state.activity,
        state.activationStep,
        state.activationSubStep,
        state.trainingStatus,
        state.leaveStatus,
        state.walletOwnershipFilter,
        state.callStatus,
        state.motorTypeIds,
        state.drivingLicenseIds,
        state.workingModelId,
        state.hasCriminalRecord,
        state.is20Old,
        state.autoValidationStatus,
        state.contractSigningStatus,
        state.personalCompanySetupStatus,
        state.zoneManagementId,
        state.zoneId,
        state.employmentType,
        state.managerCompany
    ];

    useEffect(() => {
        setUrl();
    }, urlSyncDependencies);

    useEffect(() => {
        initializeFiltersFromUrl();
    }, []);

    return {
        isAgency,
        isAdmin,
        dispatch,
        handleDateChange,
        resetFilters,
        renderCallStatusOptions,
        initializeFiltersFromUrl,
        setUrl,
        fetchCallStatuses,
        getFilterParams,

        // Dependencies
        filterDependencies,
        urlSyncDependencies,

        // Utils
        stores,
        t,
        // State
        state
    };
};
